import * as React from "react"
import { array, object, string } from "yup"
import { Grid, TextField, Alert } from "@mui/material"
import { useAppToast, useValidatorSeeker } from "@ui-seeker/app"
import { DrawerSeeker, SelectSeeker } from "@ui-seeker/core"
import { isEmpty } from '@ui-seeker/utils'

import { DIPSite, DIPSkuGroup, DIPCountry } from "@components";
import {BACKEND_APIS} from "@constants";
import {retailer} from "@signals";
import {useUserDrawer} from "./@hooks";
import {userDetailsFilters, userDrawerFilters, accessOptions} from "../@signals";



const ROLE_OPTIONS = [
    { label: 'Standard User', value: 'user' },
    { label: 'Retailer User', value: 'retailerUser' }
]

export default function UserDrawer() {

    const { showToast } = useAppToast()
    const { userFormFilters, handleUserUpdate, tableTile, isCreateMode } = useUserDrawer()

    const validatorData = useValidatorSeeker({
        userName: userFormFilters.value?.userName,
        password: userFormFilters.value?.password,
        email: userFormFilters.value?.email,
        accessValues: userFormFilters.value?.accessValues,
        sitesScopeValues: retailer.value
    }, object({
        userName: string().required('User Name must be provided'),
        password: string().required('Password must be provided'),
        email: string().required('Email must be provided'),
        ...isCreateMode && {
            accessValues: array().min(1, 'At least one access value is required'),
            sitesScopeValues: array().min(1, 'At least one site must be selected')
        }
    }))

    const drawerProps = {
        title: tableTile,
        isOpen: !isEmpty(userDrawerFilters.value),
        controller: () => {
            userDrawerFilters.value = {}
        },
        successProps: {
            onClick: isEmpty(validatorData.errors)
                ? () => {
                    handleUserUpdate()
                    userDrawerFilters.value = {}
                }
                : () => {
                    showToast.warning('Please ensure all mandatory fields are completed')
                }
        }
    }

    const customerID = userDetailsFilters.peek()?.customerID
    const isDrawerActive = !isEmpty(userDrawerFilters.value);

    return <DrawerSeeker {...drawerProps}>
        <Grid container spacing={2}>

            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="User Name"
                    size="small"
                    value={userFormFilters.value?.userName}
                    onChange={e => {
                        userFormFilters.value = {...userFormFilters.value, userName: e.target.value}
                    }}
                    sx={{ mt: 3, mb: 1 }}
                    helperText={!!isDrawerActive && validatorData.errors?.userName}
                    error={!!isDrawerActive && !!validatorData.errors?.userName}
                />
            </Grid>

            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Password"
                    size="small"
                    value={userFormFilters.value?.password}
                    onChange={e => {
                        userFormFilters.value = {...userFormFilters.value, password: e.target.value}
                    }}
                    sx={{ mb: 1 }}
                    helperText={!!isDrawerActive && validatorData.errors?.password}
                    error={!!isDrawerActive && !!validatorData.errors?.password}
                />
            </Grid>

            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Email"
                    size="small"
                    value={userFormFilters.value?.email}
                    onChange={e => {
                        userFormFilters.value = {...userFormFilters.value, email: e.target.value}
                    }}
                    sx={{ mb: 1 }}
                    helperText={!!isDrawerActive && validatorData.errors?.email}
                    error={!!isDrawerActive && !!validatorData.errors?.email}
                />
            </Grid>

            <Grid item xs={12}>
                <SelectSeeker
                    options={ROLE_OPTIONS}
                    value={userFormFilters.value?.roleValue || 'user'}
                    onChange={(e) => {
                        userFormFilters.value = {...userFormFilters.value, roleValue: e.target.value}
                    }}
                    label="Role"
                    sx={{ mb: 1 }}
                />
            </Grid>

            <Grid item xs={12}>
                <Alert
                    severity="warning"
                    variant="outlined"
                    sx={{ mb: 1.5, py: 0.5, fontSize: '0.78rem', '& .MuiAlert-message': { fontSize: '0.78rem' } }}
                >
                    Modules configured here will override the customer's default for this user.
                    Only modules assigned to the customer are available.
                </Alert>
                <SelectSeeker
                    options={accessOptions.value}
                    value={userFormFilters.value?.accessValues}
                    onChange={(e) => {
                        userFormFilters.value = {...userFormFilters.value, accessValues: e.target.value}
                    }}
                    label="Access"
                    multiple
                    sx={{ mb: 1 }}
                    error={validatorData.errors?.accessValues}
                />
            </Grid>

            <Grid item xs={12}>
                <DIPSkuGroup
                    urlAjax={`${BACKEND_APIS.MASTER_DATA}/sku-group/customer-options`}
                    value={userFormFilters.value?.skuGroupsValues}
                    onChange={(e) => {
                        userFormFilters.value = {...userFormFilters.value, skuGroupsValues: e.target.value}
                    }}
                    filters={{ customerID: customerID }}
                    sx={{ mb: 1 }}
                    multiple
                />
            </Grid>

            <Grid item xs={12}>
                <DIPCountry
                    urlAjax={`${BACKEND_APIS.MASTER_DATA}/site/market-customer-options`}
                    value={userFormFilters.value?.country}
                    onChange={(e) => {
                        userFormFilters.value = {...userFormFilters.value, country: e.target.value}
                    }}
                    sx={{ mb: 1 }}
                    filters={{ customerID: customerID }}
                    multiple
                />
            </Grid>

            <Grid item xs={12}>
                <DIPSite
                    urlAjax={`${BACKEND_APIS.MASTER_DATA}/site/customer-options`}
                    filters={{ customerID: customerID, country: userFormFilters.value?.country  }}
                    multiple
                    sx={{ mb: 1 }}
                    error={validatorData.errors?.sitesScopeValues}
                />
            </Grid>

        </Grid>
    </DrawerSeeker>
}